// Popover that lets users toggle which columns are visible in a list view.
// Preference persists per user via localStorage keyed by `storageKey`.
import { useEffect, useRef, useState } from "react";
import { Columns3, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface ColumnDef {
  key: string;
  label: string;
  /** Non-toggleable columns (always visible), e.g. row selector, name. */
  locked?: boolean;
  /** Hidden by default. User can opt in via this popover. */
  optional?: boolean;
}

interface Props {
  storageKey: string;
  columns: ColumnDef[];
  visible: Set<string>;
  onChange: (next: Set<string>) => void;
}

export function ColumnPicker({ columns, visible, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function toggle(key: string) {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        <Columns3 className="mr-1 h-3.5 w-3.5" />Columns
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-60 rounded-md border bg-popover py-1 text-sm shadow-lg">
          <div className="border-b px-3 py-1.5 text-xs text-muted-foreground">Visible columns</div>
          {columns.map((c) => {
            const isVisible = c.locked || visible.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                disabled={c.locked}
                onClick={() => !c.locked && toggle(c.key)}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-muted disabled:opacity-50 ${
                  c.locked ? "cursor-not-allowed" : ""
                }`}
              >
                <span>{c.label}{c.locked && <span className="ml-1 text-[10px] text-muted-foreground">(always)</span>}</span>
                {isVisible && <Check className="h-3.5 w-3.5 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Hook to persist a column-visibility set. */
export function usePersistentColumns(storageKey: string, defaults: Set<string>) {
  const [visible, setVisible] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return new Set<string>(JSON.parse(raw));
    } catch {}
    return new Set(defaults);
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(Array.from(visible))); }
    catch {}
  }, [storageKey, visible]);

  return [visible, setVisible] as const;
}
